import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Pill, Btn, Input, Select } from "../components/UI";
import { UserPlus, Trash2, KeyRound, CheckCircle2, XCircle, Loader2, Inbox, Copy } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "admin", "campaign_manager", "salesperson", "client_admin", "read_only", "compliance_reviewer"];

const genPassword = () => "TR-" + Math.random().toString(36).slice(2, 8) + Math.random().toString(36).slice(2, 6).toUpperCase();

export default function AdminSettings() {
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState([]);
  const [approving, setApproving] = useState(false);
  const [approveResults, setApproveResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "campaign_manager", password: "" });

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); } catch { /* permission */ }
    try { const { data } = await api.get("/access-requests"); setRequests(data); } catch { /* permission */ }
  };
  useEffect(() => { load(); }, []);

  const pending = requests.filter((r) => r.status === "pending");
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(selected.length === pending.length ? [] : pending.map((r) => r.id));

  const approve = async (ids) => {
    setApproving(true);
    setApproveResults(null);
    try {
      const { data } = await api.post("/access-requests/approve", { ids });
      setApproveResults(data.results);
      const ok = data.results.filter((r) => r.ok).length;
      const emailed = data.results.filter((r) => r.email_sent).length;
      toast.success(`${ok} approved · ${emailed} credential email(s) sent`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally { setApproving(false); }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this access request?")) return;
    await api.post(`/access-requests/${id}/reject`);
    toast.success("Request rejected");
    load();
  };

  const changeRole = async (u, role) => {
    await api.put(`/users/${u.id}`, { role });
    toast.success(`${u.name} is now ${role.replace(/_/g, " ")}`);
    load();
  };

  const resetPassword = async (u) => {
    const pw = genPassword();
    if (!window.confirm(`Reset password for ${u.email}? A new temporary password will be generated.`)) return;
    await api.put(`/users/${u.id}`, { password: pw });
    try { await navigator.clipboard.writeText(pw); } catch { /* clipboard blocked */ }
    toast.success(`New password for ${u.email}: ${pw} (copied to clipboard)`, { duration: 15000 });
  };

  const extendDemo = async (u) => {
    await api.post(`/users/${u.id}/extend-demo`);
    toast.success(`Demo extended 14 days for ${u.email}`);
    load();
  };

  const convertDemo = async (u) => {
    if (!window.confirm(`Convert ${u.email} to a full (non-expiring) account?`)) return;
    await api.post(`/users/${u.id}/convert-demo`);
    toast.success(`${u.email} converted to a full account`);
    load();
  };

  const demoDaysLeft = (u) => {
    if (!u.demo_expires_at) return null;
    return Math.ceil((new Date(u.demo_expires_at) - Date.now()) / 86400000);
  };

  const create = async () => {
    try { await api.post("/users", form); setOpen(false); setForm({ email: "", name: "", role: "campaign_manager", password: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`); load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="System Administration"
        title="Users & Access"
        subtitle="Approve access requests, manage users, roles, and platform configuration."
        action={<Btn onClick={() => setOpen(true)} data-testid="new-user-btn"><UserPlus className="w-4 h-4" /> New user</Btn>}
      />

      {/* Access requests */}
      <Card padded={false} data-testid="access-requests-card">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Inbox className="w-4 h-4 text-cyan-300" /> Access requests
            {pending.length > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30">{pending.length} pending</span>}
          </h3>
          {pending.length > 0 && (
            <Btn onClick={() => approve(selected)} disabled={!selected.length || approving} data-testid="bulk-approve-btn">
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Approve selected ({selected.length})
            </Btn>
          )}
        </div>
        {requests.length === 0 ? (
          <div className="px-5 py-8 text-center text-slate-500 text-sm">No access requests yet — signups and demo bookings will appear here.</div>
        ) : (
          <table className="w-full text-sm" data-testid="access-requests-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="px-5 py-3 w-8">
                  {pending.length > 0 && (
                    <input type="checkbox" checked={selected.length === pending.length && pending.length > 0} onChange={toggleAll} data-testid="select-all-requests" className="accent-cyan-400" />
                  )}
                </th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Organization</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.slice(0, 30).map((r) => (
                <tr key={r.id} className="border-b border-white/5" data-testid={`request-row-${r.id}`}>
                  <td className="px-5 py-2">
                    {r.status === "pending" && (
                      <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`select-request-${r.id}`} className="accent-cyan-400" />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${r.type === "demo" ? "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-400/10" : "text-cyan-300 border-cyan-400/30 bg-cyan-400/10"}`}>{r.type}</span>
                  </td>
                  <td className="px-3 py-2 text-white">{r.name}</td>
                  <td className="px-3 py-2 text-slate-300">{r.email}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{r.organization || "—"}</td>
                  <td className="px-3 py-2">
                    <Pill status={r.status === "approved" ? "Active" : r.status === "rejected" ? "Rejected" : "Pending"}>{r.status}</Pill>
                    {r.status === "approved" && r.email_sent === false && <div className="text-[10px] text-amber-300 mt-0.5">email failed</div>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.status === "pending" && (
                      <>
                        <button onClick={() => approve([r.id])} className="text-emerald-300 hover:text-emerald-200 p-1" title="Approve" data-testid={`approve-request-${r.id}`}>
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => reject(r.id)} className="text-slate-500 hover:text-rose-300 p-1" title="Reject" data-testid={`reject-request-${r.id}`}>
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {approveResults && approveResults.some((r) => r.password) && (
          <div className="px-5 py-4 border-t border-white/10 bg-emerald-400/[0.04]" data-testid="approve-results-panel">
            <div className="text-xs uppercase tracking-wider text-emerald-300 font-semibold mb-2">Demo credentials generated</div>
            {approveResults.filter((r) => r.password).map((r) => (
              <div key={r.id} className="flex items-center gap-3 text-xs py-1">
                <span className="text-slate-300">{r.email}</span>
                <code className="bg-slate-900/80 px-2 py-0.5 rounded text-cyan-300 font-mono">{r.password}</code>
                <button onClick={() => { navigator.clipboard.writeText(`${r.email} / ${r.password}`); toast.success("Copied"); }} className="text-slate-500 hover:text-cyan-300"><Copy className="w-3 h-3" /></button>
                <span className={r.email_sent ? "text-emerald-300" : "text-amber-300"}>{r.email_sent ? "✓ emailed" : `email failed${r.email_error ? `: ${r.email_error}` : ""} — share manually`}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Users */}
      <Card padded={false}>
        <div className="px-5 py-3 border-b border-white/10"><h3 className="font-display text-lg font-bold">Users & roles</h3></div>
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Role</th>
              <th className="px-5 py-3">Created</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-white/5" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-2 text-white">
                  {u.name}
                  {u.is_demo && (
                    <span className={`ml-2 text-[9px] px-1.5 py-0.5 rounded-full border ${demoDaysLeft(u) > 0 ? "bg-fuchsia-400/15 text-fuchsia-200 border-fuchsia-400/25" : "bg-rose-400/15 text-rose-200 border-rose-400/30"}`} data-testid={`demo-badge-${u.id}`}>
                      {demoDaysLeft(u) > 0 ? `demo · ${demoDaysLeft(u)}d left` : "demo · expired"}
                    </span>
                  )}
                </td>
                <td className="px-5 py-2 text-slate-300">{u.email}</td>
                <td className="px-5 py-2">
                  <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} data-testid={`role-select-${u.id}`}
                    className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-cyan-300 uppercase tracking-wider focus:outline-none focus:border-cyan-400/60">
                    {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
                <td className="px-5 py-2 text-xs font-mono text-slate-400">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-2 text-right whitespace-nowrap">
                  {u.is_demo && (
                    <>
                      <button onClick={() => extendDemo(u)} className="text-slate-500 hover:text-fuchsia-300 p-1" title="Extend demo 14 days" data-testid={`extend-demo-${u.id}`}>
                        <Inbox className="w-4 h-4" />
                      </button>
                      <button onClick={() => convertDemo(u)} className="text-slate-500 hover:text-emerald-300 p-1" title="Convert to full account" data-testid={`convert-demo-${u.id}`}>
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button onClick={() => resetPassword(u)} className="text-slate-500 hover:text-cyan-300 p-1" title="Reset password" data-testid={`reset-password-${u.id}`}>
                    <KeyRound className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(u.id)} className="text-slate-500 hover:text-rose-300 p-1" title="Delete" data-testid={`delete-user-${u.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan="5" className="px-5 py-8 text-center text-slate-500 text-sm">No users (or insufficient permission to view)</td></tr>}
          </tbody>
        </table>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Platform</div>
          <div className="space-y-2 text-sm">
            <Row k="Environment" v="Production" />
            <Row k="Region" v="us-east-1" />
            <Row k="Build" v="MVP 0.1.0" />
          </div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Data retention</div>
          <div className="space-y-2 text-sm">
            <Row k="Geo events" v="30 days" />
            <Row k="Audit logs" v="2 years" />
            <Row k="AI outputs" v="1 year" />
          </div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">AI providers</div>
          <div className="space-y-2 text-sm">
            <Row k="Strategy" v="Claude Sonnet 4.5" />
            <Row k="Image" v="Nano Banana" />
            <Row k="Key" v="Universal AI Key" />
          </div>
        </Card>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#0a0e1a] border border-white/10 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-4">New user</h3>
            <div className="space-y-3">
              <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
              <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
              <Input label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
              <Select label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
              </Select>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn onClick={create} data-testid="save-user-btn">Create user</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-white/5 py-1.5">
    <span className="text-slate-400">{k}</span>
    <span className="text-white font-mono text-xs">{v}</span>
  </div>
);
